import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { spacing } from "#app/theme/v2/spacing";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { InputSuffix } from "#components/atoms/InputSuffix/InputSuffix";
import { Text } from "#components/atoms/Typographies/Text";
import { NestingItem, NestingItemsTable } from "#modules/layoutsV2/view/NestingItemsTable";
import { NestingSheetImage } from "#modules/layoutsV2/view/NestingSheetImage";
import { useNestingSvg } from "#modules/layoutsV2/view/useNestingSvg";
import { Icon } from "#shared/icons/Icon";
import { Box } from "#shared/view/components/Box";
import { Flex } from "#shared/view/components/Flex";
import { NestingRunView } from "shared";

type Props = {
  items: NestingItem[];
  layouts: NestingRunView["layouts"];
  totalSheets: number;
  billedShare: string;
  onBilledShareChange: (value: string) => void;
  onBilledShareBlur: () => void;
  isMaterialCostLoading: boolean;
  onSvgUrlExpired: () => void;
  renderRowAction?: (itemId: string) => ReactNode;
};

export const NestingPlanView = ({
  items,
  layouts,
  totalSheets,
  billedShare,
  onBilledShareChange,
  onBilledShareBlur,
  isMaterialCostLoading,
  onSvgUrlExpired,
  renderRowAction,
}: Props) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState(0);

  const sheets = layouts.flatMap((layout) =>
    layout.sheetIndices.map((sheetIndex) => ({ layout, sheetIndex })),
  );
  const currentPosition = Math.min(position, Math.max(sheets.length - 1, 0));
  const currentSheet = sheets[currentPosition];
  const totalPieces = layouts.reduce((sum, { pieceCount, count }) => sum + pieceCount * count, 0);
  const svg = useNestingSvg({
    svgUrl: currentSheet?.layout.svgUrl ?? "",
    itemIds: items.map(({ id }) => id),
    isEnabled: items.length > 1 && currentSheet !== undefined,
    onUrlExpired: onSvgUrlExpired,
  });

  if (!currentSheet) {
    return null;
  }

  const { layout: currentLayout, sheetIndex } = currentSheet;

  return (
    <>
      <Flex direction="column" gap={spacing[3]}>
        <Flex align="center" justify="space-between">
          <Text size="small">
            {t("layout.sidePanel.sheetProgress", {
              current: sheetIndex + 1,
              total: totalSheets,
              pieces: currentLayout.pieceCount,
              totalPieces,
            })}
          </Text>
          <Flex gap={spacing[2]}>
            <IconButton
              variant="tertiary"
              aria-label={t("layout.trueShapeNesting.previous")}
              disabled={currentPosition === 0}
              onClick={() => setPosition(currentPosition - 1)}
            >
              <Icon name="chevronLeft" size={16} />
            </IconButton>
            <IconButton
              variant="tertiary"
              aria-label={t("layout.trueShapeNesting.next")}
              disabled={currentPosition === sheets.length - 1}
              onClick={() => setPosition(currentPosition + 1)}
            >
              <Icon name="chevronRight" size={16} />
            </IconButton>
          </Flex>
        </Flex>

        <Flex gap={spacing[4]} align="flex-start">
          <Box flex={1} style={{ minWidth: 0 }}>
            <NestingSheetImage
              status={svg.status}
              markup={svg.markup}
              svgUrl={currentLayout.svgUrl}
              alt={t("layout.trueShapeNesting.svgAlt", { sheets: sheetIndex + 1 })}
              onImageError={svg.onImageError}
              onRetry={svg.retry}
            />
          </Box>
        </Flex>
      </Flex>

      <Flex align="center" gap={spacing[2]}>
        <Text size="small" type="medium" style={{ whiteSpace: "nowrap" }}>
          {t("layout.sidePanel.billedShare")}
        </Text>
        <Box w={85}>
          <InputSuffix
            value={billedShare}
            onChange={onBilledShareChange}
            onBlur={onBilledShareBlur}
            suffix="%"
            aria-label={t("layout.sidePanel.billedShare")}
          />
        </Box>
      </Flex>

      <NestingItemsTable
        items={items}
        isMaterialCostLoading={isMaterialCostLoading}
        hasItemColors={svg.status !== "fallback" && svg.status !== "error"}
        renderRowAction={renderRowAction}
      />
    </>
  );
};
